import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import type { ItemStatus } from "../types";

import useToast from "../../../../hooks/useToast";

import { meetingDetailsQueryKeys } from "../query-keys";

type ChecklistItemMeeting = {
	meetingId: string;
	status: "complete" | "incomplete" | "deleted";
};

type ChecklistItemData = {
	_id: string;
	name: string;
	meetings: ChecklistItemMeeting[];
	created?: { userId?: string };
	participants?: Array<{ userId: string; role: string }>;
	dueDate?: Date | string | null;
	goalId?: string | null;
};

type MutationCallbacks = {
	onSuccess?: () => void;
	onError?: () => void;
	onSettled?: () => void;
};

type AgendaAddParams = {
	data: {
		name: string;
		meetings: ChecklistItemMeeting[];
	};
};

type ActionAddParams = {
	data: {
		name: string;
		meetings: ChecklistItemMeeting[];
		participants: never[];
		dueDate: null;
	};
};

type AgendaEditParams = {
	agendaItemId: string;
	data: Record<string, unknown>;
};

type ActionEditParams = {
	actionItemId: string;
	data: Record<string, unknown>;
};

type AgendaAddMutationFn = (params: AgendaAddParams, options?: MutationCallbacks) => void;
type ActionAddMutationFn = (params: ActionAddParams, options?: MutationCallbacks) => void;
type AgendaEditMutationFn = (params: AgendaEditParams, options?: MutationCallbacks) => void;
type ActionEditMutationFn = (params: ActionEditParams, options?: MutationCallbacks) => void;

/**
 * Parameters for the useChecklistItems hook.
 * @param meetingId - The ID of the current meeting
 * @param itemType - The type of checklist item ('agenda' or 'action')
 * @param items - The list of checklist items from the query
 * @param isLoading - Whether the items are currently loading
 * @param addItemMutation - The mutation function to add a new item
 * @param editItemMutation - The mutation function to edit an existing item
 * @param isInteractionDisabled - Whether interactions are disabled (e.g., admin view)
 */
type UseChecklistItemsParams =
	| {
			meetingId: string;
			itemType: "agenda";
			items: ChecklistItemData[] | undefined;
			isLoading: boolean;
			addItemMutation: AgendaAddMutationFn;
			editItemMutation: AgendaEditMutationFn;
			isInteractionDisabled?: boolean;
	  }
	| {
			meetingId: string;
			itemType: "action";
			items: ChecklistItemData[] | undefined;
			isLoading: boolean;
			addItemMutation: ActionAddMutationFn;
			editItemMutation: ActionEditMutationFn;
			isInteractionDisabled?: boolean;
	  };

type UseChecklistItemsReturn = {
	activeItems: ChecklistItemData[];
	isAddingItem: boolean;
	newItemName: string;
	editingItemId: string | null;
	editingItemCurrentValue: string;
	isLoading: boolean;
	handleEditStart: (itemId: string, currentValue: string) => void;
	handleEditStop: (save: boolean) => void;
	handleNameChange: (value: string) => void;
	handleNewItemNameChange: (value: string) => void;
	handleStatusChange: (itemId: string, status: ItemStatus) => void;
	handleAddButtonClick: () => void;
	handleNewItemSave: (save: boolean) => void;
};

/**
 * Shared hook for managing checklist items (agenda items and action items).
 * Handles state management, editing, adding, and status changes with optimistic updates.
 */
export const useChecklistItems = (params: UseChecklistItemsParams): UseChecklistItemsReturn => {
	const { meetingId, itemType, items, isLoading, isInteractionDisabled = false } = params;

	const queryClient = useQueryClient();
	const toast = useToast();

	const [isAddingItem, setIsAddingItem] = useState(false);
	const [newItemName, setNewItemName] = useState("");
	const [editingItemId, setEditingItemId] = useState<string | null>(null);
	const [editingItemCurrentValue, setEditingItemCurrentValue] = useState("");
	const [originalItemValue, setOriginalItemValue] = useState("");

	const itemTypeLabel = itemType === "agenda" ? "Agenda item" : "Action item";
	const queryKey =
		itemType === "agenda"
			? meetingDetailsQueryKeys.meetingAgendaItems(meetingId)
			: meetingDetailsQueryKeys.meetingActionItems(meetingId);

	const handleSaveEdit = (itemId: string, newName: string): void => {
		const trimmedName = newName.trim();
		const currentItem = items?.find((item) => item._id === itemId);

		if (currentItem && currentItem.name === trimmedName) {
			return;
		}

		const previousItems = items || [];
		const optimisticItems = previousItems.map((item) =>
			item._id === itemId ? { ...item, name: trimmedName } : item
		);

		queryClient.setQueryData(queryKey, () => [optimisticItems]);

		const callbacks: MutationCallbacks = {
			onSuccess: () => {
				toast(`${itemTypeLabel} updated`, { type: "success", size: "large" });
			},
			onError: () => {
				queryClient.setQueryData(queryKey, [previousItems]);
				toast(`Failed to update ${itemTypeLabel.toLowerCase()}`, { type: "error", size: "large" });
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey });
			},
		};

		if (itemType === "agenda") {
			(params.editItemMutation as AgendaEditMutationFn)(
				{ agendaItemId: itemId, data: { name: trimmedName } },
				callbacks
			);
		} else {
			(params.editItemMutation as ActionEditMutationFn)(
				{ actionItemId: itemId, data: { name: trimmedName } },
				callbacks
			);
		}
	};

	const handleAddItem = (name: string): void => {
		if (isInteractionDisabled) return;

		const trimmedName = name.trim();
		const tempId = `temp-${Date.now()}`;

		setIsAddingItem(false);
		setNewItemName("");

		const baseData = {
			name: trimmedName,
			meetings: [{ meetingId, status: "incomplete" as const }],
		};

		const previousItems = items || [];

		if (itemType === "agenda") {
			const newItem = {
				_id: tempId,
				name: trimmedName,
				meetings: baseData.meetings,
				created: { userId: "" },
			};

			queryClient.setQueryData(queryKey, () => [[...previousItems, newItem]]);

			(params.addItemMutation as AgendaAddMutationFn)(
				{ data: baseData },
				{
					onSuccess: () => {
						toast(`${itemTypeLabel} created`, { type: "success", size: "large" });
					},
					onError: () => {
						queryClient.setQueryData(queryKey, [previousItems]);
						toast(`Failed to create ${itemTypeLabel.toLowerCase()}`, { type: "error", size: "large" });
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey });
					},
				}
			);
		} else {
			const newItem = {
				_id: tempId,
				name: trimmedName,
				meetings: baseData.meetings,
				participants: [],
				dueDate: null,
				created: { userId: "" },
			};

			queryClient.setQueryData(queryKey, () => [[...previousItems, newItem]]);

			(params.addItemMutation as ActionAddMutationFn)(
				{ data: { ...baseData, participants: [], dueDate: null } },
				{
					onSuccess: () => {
						toast(`${itemTypeLabel} created`, { type: "success", size: "large" });
					},
					onError: () => {
						queryClient.setQueryData(queryKey, [previousItems]);
						toast(`Failed to create ${itemTypeLabel.toLowerCase()}`, { type: "error", size: "large" });
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey });
					},
				}
			);
		}
	};

	const handleStatusChange = (itemId: string, status: ItemStatus): void => {
		if (isInteractionDisabled) return;

		const currentItem = items?.find((item) => item._id === itemId);
		if (!currentItem) return;

		const updatedMeetings = currentItem.meetings.map((m) =>
			m.meetingId === meetingId ? { ...m, status: status as ChecklistItemMeeting["status"] } : m
		);

		const previousItems = items || [];
		const optimisticItems = previousItems.map((item) =>
			item._id === itemId ? { ...item, meetings: updatedMeetings } : item
		);

		queryClient.setQueryData(queryKey, () => [optimisticItems]);

		// Only show toast for delete action, not for check/uncheck
		const isDelete = status === "deleted";
		const callbacks: MutationCallbacks = {
			onSuccess: () => {
				if (isDelete) {
					toast(`${itemTypeLabel} deleted`, { type: "success", size: "large" });
				}
			},
			onError: () => {
				queryClient.setQueryData(queryKey, [previousItems]);
				toast(`Failed to update ${itemTypeLabel.toLowerCase()}`, { type: "error", size: "large" });
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey });
			},
		};

		if (itemType === "agenda") {
			(params.editItemMutation as AgendaEditMutationFn)(
				{ agendaItemId: itemId, data: { meetings: updatedMeetings } },
				callbacks
			);
		} else {
			(params.editItemMutation as ActionEditMutationFn)(
				{ actionItemId: itemId, data: { meetings: updatedMeetings } },
				callbacks
			);
		}
	};

	const handleEditStart = (itemId: string, currentValue: string): void => {
		if (isInteractionDisabled) return;

		if (isAddingItem && newItemName.trim()) {
			handleAddItem(newItemName);
		} else if (isAddingItem) {
			setIsAddingItem(false);
			setNewItemName("");
		}

		if (editingItemId && editingItemId !== itemId) {
			if (editingItemCurrentValue.trim() !== "") {
				handleSaveEdit(editingItemId, editingItemCurrentValue);
			} else {
				const previousItems = items || [];
				const optimisticItems = previousItems.map((item) =>
					item._id === editingItemId ? { ...item, name: originalItemValue } : item
				);
				queryClient.setQueryData(queryKey, () => [optimisticItems]);
			}
		}

		setEditingItemId(itemId);
		setEditingItemCurrentValue(currentValue);
		setOriginalItemValue(currentValue);
	};

	const handleEditStop = (save: boolean): void => {
		if (save && editingItemId && editingItemCurrentValue.trim()) {
			handleSaveEdit(editingItemId, editingItemCurrentValue);
		}
		setEditingItemId(null);
		setEditingItemCurrentValue("");
		setOriginalItemValue("");
	};

	const handleNameChange = (value: string): void => {
		setEditingItemCurrentValue(value);
	};

	const handleNewItemNameChange = (value: string): void => {
		setNewItemName(value);
	};

	const handleAddButtonClick = (): void => {
		if (isInteractionDisabled) return;

		if (isAddingItem && newItemName.trim()) {
			const currentItemName = newItemName;
			setNewItemName("");
			handleAddItem(currentItemName);
			setIsAddingItem(false);
			setTimeout(() => setIsAddingItem(true), 0);
			return;
		}

		setIsAddingItem(true);
	};

	const handleNewItemSave = (save: boolean): void => {
		if (save && newItemName.trim()) {
			handleAddItem(newItemName);
		} else {
			setIsAddingItem(false);
			setNewItemName("");
		}
	};

	const activeItems = useMemo(() => {
		return (
			items?.filter((item) => {
				const meetingItem = item.meetings.find((m) => m.meetingId === meetingId);
				return meetingItem?.status !== "deleted";
			}) || []
		);
	}, [items, meetingId]);

	return {
		activeItems,
		isAddingItem,
		newItemName,
		editingItemId,
		editingItemCurrentValue,
		isLoading,
		handleEditStart,
		handleEditStop,
		handleNameChange,
		handleNewItemNameChange,
		handleStatusChange,
		handleAddButtonClick,
		handleNewItemSave,
	};
};
